import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { useUpdateProjectValue } from "@/features/finances/hooks/useFinances";
import { ProjectStatusActions } from "@/features/enterprises/components/ProjectStatusActions";
import type { Project } from "@/features/enterprises/types";
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_COLORS } from "@/features/enterprises/types";
import { useAuthStore } from "@/store/auth";

interface Props {
  project: Project;
  /** Edycja wartości / terminów (update-value/): dyrektor lub księgowość z dostępem finansowym. */
  canEdit: boolean;
  /** Wstrzymanie / wznowienie (set-status/): CEO / dyrektor techniczny / przypisany koordynator — NIE księgowość. */
  canSetStatus: boolean;
  /** Dezaktywacja (inactive): CEO lub dyrektor techniczny z dostępem finansowym — koordynator NIE. */
  canDeactivate: boolean;
}

export function ProjectStatusControl({ project, canEdit, canSetStatus, canDeactivate }: Props) {
  const user = useAuthStore((s) => s.user);
  const isCeo = user?.is_ceo === true;

  const updateValue = useUpdateProjectValue(project.id);

  const [showValueForm, setShowValueForm] = useState(false);
  const [valueForm, setValueForm] = useState({
    total_value: project.total_value ?? "",
    planned_end_date: project.planned_end_date ?? "",
  });

  const [showCeoDateForm, setShowCeoDateForm] = useState(false);
  const [ceoDateInput, setCeoDateInput] = useState(project.planned_end_date_ceo ?? "");

  const isDirector = user?.role === "director";
  const canEditDirectorDate = isCeo || (isDirector && user?.is_technical_director === true);
  const showDirectorDate = isCeo || isDirector;
  const [showDirectorDateForm, setShowDirectorDateForm] = useState(false);
  const [directorDateInput, setDirectorDateInput] = useState(project.planned_end_date_director ?? "");

  const handleUpdateValue = async () => {
    await updateValue.mutateAsync({
      total_value: valueForm.total_value || null,
      planned_end_date: valueForm.planned_end_date || null,
    });
    setShowValueForm(false);
  };

  const handleUpdateCeoDate = async () => {
    await updateValue.mutateAsync({ planned_end_date_ceo: ceoDateInput || null });
    setShowCeoDateForm(false);
  };

  const handleUpdateDirectorDate = async () => {
    await updateValue.mutateAsync({ planned_end_date_director: directorDateInput || null });
    setShowDirectorDateForm(false);
  };

  const isSuspended = project.status === "suspended";

  return (
    <div className="bg-white dark:bg-gray-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">Status przedsięwzięcia</h3>
            <Badge color={PROJECT_STATUS_COLORS[project.status]}>
              {PROJECT_STATUS_LABELS[project.status]}
            </Badge>
            {isSuspended && (
              <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
                {Math.round(parseFloat(project.suspension_fraction) * 100)}% wstrzymane
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Wartość: <strong className="text-gray-800 dark:text-gray-200">
                {project.total_value ? `${formatAmount(project.total_value)} PLN` : "—"}
              </strong>
            </span>
            <span>
              Planowane zakończenie: <strong className="text-base font-bold text-gray-800 dark:text-gray-200">
                {formatDate(project.planned_end_date)}
              </strong>
            </span>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setValueForm({ total_value: project.total_value ?? "", planned_end_date: project.planned_end_date ?? "" });
              setShowValueForm(!showValueForm);
            }}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            {showValueForm ? "Anuluj" : "Edytuj wartość / datę"}
          </button>
        )}
      </div>

      {/* Directors + CEO: planned_end_date_director display + edit */}
      {showDirectorDate && (
        <div className="mt-2 mb-1 flex items-center gap-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/40 rounded px-3 py-2">
          <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide select-none">
            DYR
          </span>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Planowane zakończenie (Dyrektorzy):{" "}
            <strong className="text-gray-900 dark:text-gray-100">
              {formatDate(project.planned_end_date_director)}
            </strong>
          </span>
          {canEditDirectorDate && (
            <button
              className="ml-auto text-xs text-blue-700 dark:text-blue-400 hover:underline"
              onClick={() => { setDirectorDateInput(project.planned_end_date_director ?? ""); setShowDirectorDateForm((v) => !v); }}
            >
              {showDirectorDateForm ? "Anuluj" : "Edytuj"}
            </button>
          )}
        </div>
      )}
      {canEditDirectorDate && showDirectorDateForm && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/40 rounded px-3 py-3 mb-3 flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-blue-700 dark:text-blue-400 block mb-1">
              Planowane zakończenie (Dyrektorzy)
            </label>
            <DatePicker
              className="border-blue-300 dark:border-blue-700"
              value={directorDateInput}
              onChange={setDirectorDateInput}
            />
          </div>
          <button
            className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={updateValue.isPending}
            onClick={handleUpdateDirectorDate}
          >
            Zapisz
          </button>
          {project.planned_end_date_director && (
            <button
              className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={updateValue.isPending}
              onClick={async () => { await updateValue.mutateAsync({ planned_end_date_director: null }); setShowDirectorDateForm(false); }}
            >
              Usuń
            </button>
          )}
        </div>
      )}

      {/* CEO-only: planned_end_date_ceo display + edit */}
      {isCeo && (
        <div className="mt-2 mb-3 flex items-center gap-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded px-3 py-2">
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide select-none">
            CEO
          </span>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Planowane zakończenie (CEO):{" "}
            <strong className="text-gray-900 dark:text-gray-100">
              {formatDate(project.planned_end_date_ceo)}
            </strong>
          </span>
          <button
            className="ml-auto text-xs text-amber-700 dark:text-amber-400 hover:underline"
            onClick={() => { setCeoDateInput(project.planned_end_date_ceo ?? ""); setShowCeoDateForm((v) => !v); }}
          >
            {showCeoDateForm ? "Anuluj" : "Edytuj"}
          </button>
        </div>
      )}
      {isCeo && showCeoDateForm && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded px-3 py-3 mb-3 flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-amber-700 dark:text-amber-400 block mb-1">
              Planowane zakończenie CEO (poufne)
            </label>
            <DatePicker
              className="border-amber-300 dark:border-amber-700"
              value={ceoDateInput}
              onChange={setCeoDateInput}
            />
          </div>
          <Button size="sm" loading={updateValue.isPending} onClick={handleUpdateCeoDate}>Zapisz</Button>
          {project.planned_end_date_ceo && (
            <Button size="sm" variant="secondary" loading={updateValue.isPending}
              onClick={async () => { await updateValue.mutateAsync({ planned_end_date_ceo: null }); setShowCeoDateForm(false); }}
            >
              Usuń
            </Button>
          )}
        </div>
      )}

      {/* Value / end date form */}
      {showValueForm && canEdit && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Wartość projektu (PLN)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={valueForm.total_value}
              onChange={(e) => setValueForm({ ...valueForm, total_value: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Planowane zakończenie</label>
            <DatePicker
              value={valueForm.planned_end_date}
              onChange={(v) => setValueForm({ ...valueForm, planned_end_date: v })}
            />
          </div>
          <div className="col-span-2 flex gap-2">
            <Button size="sm" loading={updateValue.isPending} onClick={handleUpdateValue}>Zapisz</Button>
            <Button size="sm" variant="secondary" onClick={() => setShowValueForm(false)}>Anuluj</Button>
          </div>
        </div>
      )}

      {/* Status actions — CEO / dyrektor techniczny / przypisany koordynator; księgowość tylko czyta */}
      {canSetStatus && (
        <div className="mt-2">
          <ProjectStatusActions project={project} canDeactivate={canDeactivate} />
        </div>
      )}
    </div>
  );
}
